import { animate, style, transition, trigger } from '@angular/animations';
import { Dialog } from '@angular/cdk/dialog';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { CLIENT_SEARCH_OPTIONS, CLIENT_SORTING_COLUMN, convertToCapitalCase, convertToTitleCase, PAGINATION_DATA, paginationChange, PROJECT_SEARCH_OPTIONS, PROJECT_SORT_COLUMNS, VENDOR_SEARCH_OPTIONS, VENDOR_SORT_COLUMNS } from '@common/interfaces';
import { FIELD_ARRAY_CLIENTS_LIST, FIELD_ARRAY_PROJECTS_LIST } from 'libs/common/src/lib/constants/table-field-constants';
import { IRole, IworkspaceOwnerDetails } from 'libs/common/src/lib/interfaces/interface';
import { CommonHttpService } from 'libs/common/src/lib/service/common-http.service';
import { CommonService } from 'libs/common/src/lib/service/common.service';
import { ConfirmationPopupWithFormComponent } from 'libs/shared/ui-components/src/lib/confirmation-popup-with-form/confirmation-popup-with-form.component';
import { PaginationComponent } from 'libs/shared/ui-components/src/lib/pagination/pagination.component';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, filter, Subject } from 'rxjs';
import { AccessoryTypeDetailComponent } from '../accessory-type-detail/accessory-type-detail.component';
import { AddAccessoryTypeComponent } from '../add-accessory-type/add-accessory-type.component';
import { AddAssetTypeComponent } from '../add-asset-type/add-asset-type.component';
import { AssetTypeDetailComponent } from '../asset-type-detail/asset-type-detail.component';
import { UserDetailsComponent } from '../user-details/user-details.component';
@Component({
  selector: 'app-manage-workspace-owner',
  templateUrl: './manage-workspace-owner.component.html',
  styleUrls: ['./manage-workspace-owner.component.scss'],
  animations: [
    trigger('basicAnimation', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('500ms', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('100ms', style({ opacity: 0 }))]),
    ]),
  ],
})
export class ManageWorkspaceOwnerComponent implements OnInit {
  companyLink = '';
  workspaceId: string = '';
  placeholderTitle = 'No asset types assigned';
  placeholderSubtitle = '';
  searchColumn!: string;
  searchText!: string;
  pageSize = '5';
  workspaceProjectDetails: any;
  status: string = '';
  descendingSortIcon = `assets/12x12/sort-asc.svg`;
  ascendingSortIcon = `assets/12x12/sort-desc.svg`;
  defaultSortIcon = 'assets/12x12/sort-desc.svg';
  currentSortOrder = 'DESC';
  sortIcons = [{ typeOfOrder: 'DESC', icon: `assets/12x12/sort-asc.svg` }, { typeOfOrder: 'ASC', icon: 'assets/12x12/sort-desc.svg' }];
  imageAddress = this.sortIcons[0].icon;
  displayedColumns: string[] = [
    'projects',
    'assetsLinked',
    'createdBy',
    'createdOn',
    'edit',
  ];
  flagClientAdded = false;
  displayedColumnsCount = {
    projects: 0,
    assetsLinked: 0,
  };
  tableConfig = [
    {
      title: 'Client name',
      hasMultiKeys: false,
      sortAction: (col: string) => {
        this.handleSortAction(col);
      },
      icon: 'sort-asc',
      config: {
        keys: { key: 'clientName', from: 'project' },
        isProject: true,
      },
    },
    {
      title: 'Projects',
      hasMultiKeys: false,
      sortAction: (col: string) => {
        this.handleSortAction(col);
      },
      icon: 'sort-asc',
      config: {
        keys: { key: 'name', from: 'project' },
        isProject: true,
      },
    },
    {
      title: 'Assets Linked',
      hasMultiKeys: false,
      sortAction: (col: string) => {
        this.handleSortAction(col);
      },
      icon: 'sort-asc',
      config: {
        keys: { key: 'assetLinked', from: 'project' },
        isAssetsLinked: true,
      },
    },
    {
      title: 'Created By',
      hasMultiKeys: false,
      sortAction: (col: string) => {
        this.handleSortAction(col);
      },
      icon: 'sort-asc',
      config: {
        keys: { key: 'createdByUser', from: 'user' },
        isCreatedBy: true,
      },
    },
    {
      title: 'Created On',
      hasMultiKeys: false,
      sortAction: (col: string) => {
        this.handleSortAction(col);
      },
      icon: 'sort-asc',
      config: {
        keys: { key: 'createdOn', from: 'project', isDate: true },
        isCreatedOn: true,
      },
    },
    {
      title: 'Actions',
      hasMultiKeys: false,
      sortAction: null, // No sort action for edit button
      icon: '',
      config: {
        keys: [],
        isActionColumn: true,
        actions: [
          {
            label: 'Edit',
            icon: 'edit_blue.svg',
            onClick: (project: any) => this.onEditProject(project)
          },
          {
            label: 'Delete',
            icon: 'delete_blue.svg',
            onCLick: (project: any) => this.onEditProject(project)
          }
        ]
      },
    },
  ];
  clientTableConfig = [
  {
    title: 'Client Name',
    hasMultiKeys: false,
    sortAction: (col: string) => {
      this.handleClientSortAction(col);
    },
    icon: 'sort-asc',
    config: {
      keys: { key: 'clientName', from: 'client' },
      isClientName: true,
    },
  },
  {
    title: 'Created At',
    hasMultiKeys: false,
    sortAction: (col: string) => {
      this.handleClientSortAction(col);
    },
    icon: 'sort-asc',
    config: {
      keys: { key: 'createdAt', from: 'client', isDate: true },
      isClientCreatedAt: true,
    },
  },
  {
    title: 'Created By',
    hasMultiKeys: false,
    sortAction: (col: string) => {
      this.handleClientSortAction(col);
    },
    icon: 'sort-asc',
    config: {
      keys: { key: 'createdByUser', from: 'user' }, 
      isClientCreatedBy: true,
    },
  },
];

  editButtonImage: string = 'assets/14x14/edit_blue.svg';
  showAssetDetails: boolean = false;
  tableDetails: any;
  @ViewChild('manage workspace') _workspaceForPagination!: PaginationComponent;
  @ViewChild('projectsSection') projectsSection!: ElementRef;
  @ViewChild('vendorSection') vendorSection!: ElementRef;
  @ViewChild('clientSection') clientSection!: ElementRef;
  @Output() paginatorEmitter = new EventEmitter<any>();
  @ViewChild(AddAssetTypeComponent) addAssetTypeComponent!: AddAssetTypeComponent;
  @ViewChild(AddAccessoryTypeComponent) addAccessoryTypeComponent!: AddAccessoryTypeComponent;
  dataSource: any[] = [];
  currentPageNumber = '1';
  projectId: string = '';
  count = 0; // Total number of records
  pageCount = '5'; // Default page size
  reload = new EventEmitter<boolean>();
  pageSizeOptions = ['5', '10', '15', '20'];
  selectedPageSize: any;
  workspaceOwnerDetails: IworkspaceOwnerDetails | null = null;
  showAddAssetType = false;
  showAddAccessoryType = false;
  PAGINATION_DATA = PAGINATION_DATA;
  fieldArray = FIELD_ARRAY_PROJECTS_LIST;
  fieldArrayClient = FIELD_ARRAY_CLIENTS_LIST;
  userTabs = [
    { label: 'Workspace Owners', value: 'WORKSPACE-OWNER', count: 0 },
    { label: 'IT Admins', value: 'ADMIN', count: 0 },
    { label: 'End Users', value: 'USER', count: 0 },
  ];
  selectedUserTab = 'WORKSPACE-OWNER';
  userDisplayedColumns: string[] = [
    'name',
    'employeeId',
    'designation',
    'reportingManager',
  ];
  userSearchOptions = [
    { title: 'Name', value: 'NAME' },
    { title: 'Employee ID', value: 'EMP_ID' },
    { title: 'Email', value: 'EMAIL_ID' },
    // { title: 'Designation', value: 'DESIGNATION' }
  ];
  usersDataSource: any[] = [];
  userCount = 0;
  userPageCount = '5';
  page: number = 1;
  addFormData: any;
  PROJECT_SEARCH_OPTIONS = Object.values(PROJECT_SEARCH_OPTIONS);
  CLIENT_SEARCH_OPTIONS = Object.values(CLIENT_SEARCH_OPTIONS);
  VENDOR_SEARCH_OPTIONS = Object.values(VENDOR_SEARCH_OPTIONS);
  userReload = new EventEmitter<boolean>();
  isSearching: boolean = false;
  isClientSearching: boolean = false;
  workplaceDetailsLink: string | null = null;
  loaderView: boolean = false;
  assetTypeId!: string;
  assetDetails: any;
  currentSort:string=''
  private searchSubject = new Subject<any>();
  userId: string | null = null;
  sortDigit = 'DESC';
  vendorsDataSource: any[] = [];
  vendorCount = 0;
  vendorPageCount = '5';
  phoneError: string = '';
  clientTableDetails: any[] = [];
  clientCount = 0;
  clientPage: number = 1;
  clientPageCount = '5';
  clientTotalPages: number = 0;
  vendorTotalPages: number = 0; 
  vendorPage: number = 1;
  isVendorSearching: boolean = false;
  clientsList: any[] =[];
  addClientClick$ = new Subject<void>();
  isAddingClient: boolean = false;
  isProjectModalLoading = false;
  constructor(
    private commonHttpService: CommonHttpService,
    private commonService: CommonService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private toastrService: ToastrService,
    private cdr: ChangeDetectorRef,
    private dialog: Dialog,
  ) {
    this.selectedPageSize = this.pageSizeOptions[0];
  }
  ngOnInit(): void {
    this.searchSubject.pipe(debounceTime(900)).subscribe((searchData) => {
      this.getWorkspaceOwnersProjects({
        pageNumber: this.page,
        itemsPerPage: this.pageCount,
        searchColumn: this.searchColumn,
        searchText: this.searchText,
      });
    });

    this.workspaceId = localStorage.getItem('workspaceId') as string;
    this.getWorkspaceOwnerDetails(this.workspaceId);
    this.getWorkspaceOwnersVendors();
    this.getAllClients();
    this.getWorkspaceOwnersClients();
    this.updateTabCounts();

    const pageCount = sessionStorage.getItem('pageSize');
    if (pageCount) {
      this.pageCount = pageCount;
    }

    this.getUsersList();
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.checkCurrentRoute();
      this.getWorkspaceOwnerDetails(this.workspaceId);

    });

    // Initial check
    this.checkCurrentRoute();

    this.activatedRoute.paramMap.subscribe(params => {
      this.assetTypeId = params.get('id') || '';
    });
  }

  handleSortAction(col: string) {
    const sortAction = col.split(' ');
    const columnName = sortAction[1];
    const sortOrder = sortAction[0];
    this.sortDigit = sortOrder;
    const param = {
      sortColumn: '',
      sortDirection: this.sortDigit,
      pageNumber: this.currentPageNumber
    };
    switch (columnName) {
      case 'Client':
        param.sortColumn = PROJECT_SORT_COLUMNS.CLIENT_NAME;
        break;
      case 'Projects':
        param.sortColumn = PROJECT_SORT_COLUMNS.PROJECT_NAME;
        break;

      case 'AddedBy':
        param.sortColumn = PROJECT_SORT_COLUMNS.CREATED_BY;
        break;

      case 'AddedOn':
        param.sortColumn = PROJECT_SORT_COLUMNS.CREATED_ON;
        break;

      case 'AssetsLinked':
        param.sortColumn = PROJECT_SORT_COLUMNS.ASSETS_LINKED;
        break;

      default:
        param.sortColumn = '';
        break
    }
    this.getWorkspaceOwnersProjects(param);
  }

  handleClientSortAction(col: string) {
    const sortAction = col.split(' ');
    const columnName = sortAction[1];
    const sortOrder = sortAction[0];
    this.sortDigit = sortOrder;
    const param = {
      sortColumn: '',
      sortDirection: this.sortDigit,
      pageNumber: this.currentPageNumber
    };
    switch (columnName) {
      case 'ClientName':
        param.sortColumn = CLIENT_SORTING_COLUMN.CLIENT_NAME;
        break;

      case 'AddedAt':
        param.sortColumn = CLIENT_SORTING_COLUMN.CREATED_AT;
        break;

      case 'AddedBy':
        param.sortColumn = CLIENT_SORTING_COLUMN.CREATED_BY;
        break;

      default:
        param.sortColumn = '';
        break
    }
    this.getWorkspaceOwnersClients(param);
  }
  getImageAddress(columnName: string): string {
  const [currentColumn, sortDirection] = this.currentSort.split(' ');

  if (currentColumn === columnName) {
    if (sortDirection === 'DESC') {
      return this.descendingSortIcon;
    } else if (sortDirection === 'ASC') {
      return this.ascendingSortIcon;
    }
  }
  return this.defaultSortIcon;
}

 handleVendorSortAction(col: string) {
  const columnName = col.split(' ')[0];

  let newSortOrder = 'DESC';
  if (this.currentSort.startsWith(columnName)) {
    const lastSortOrder = this.currentSort.split(' ')[1];
    newSortOrder = lastSortOrder === 'ASC' ? 'DESC' : 'ASC';
  }
  this.currentSort = `${columnName} ${newSortOrder}`;
  const param = {
    sortColumn: '',
    sortDirection: newSortOrder,
    pageNumber: this.currentPageNumber
  };
  switch (columnName) {
    case 'name':
      param.sortColumn = VENDOR_SORT_COLUMNS.VENDOR_NAME;
      break;
    case 'email':
      param.sortColumn = VENDOR_SORT_COLUMNS.VENDOR_EMAIL;
      break;
    case 'phoneNumber':
      param.sortColumn = VENDOR_SORT_COLUMNS.VENDOR_PHONE_NUMBER;
      break;
    default:
      param.sortColumn = '';
      break;
  }

  this.getWorkspaceOwnersVendors(param);
}

  checkCurrentRoute() {
    const currentRoute = this.activatedRoute.snapshot.firstChild;
    this.showAddAssetType = currentRoute?.routeConfig?.path === 'add-asset-type';
    this.showAddAccessoryType = currentRoute?.routeConfig?.path === 'add-accessory-type';
  }
  onPageSizeChange(event: any): void {
    if (!this.isSearching) {
      this.pageCount = event;
      sessionStorage.setItem('pageSize', event);
      this.getWorkspaceOwnersProjects({
        pageNumber: this.page,
        itemsPerPage: event,
      });
    } else {
      this.pageCount = event!;
      sessionStorage.setItem('pageSize', event);
      this.getWorkspaceOwnersProjects({
        pageNumber: this.page,
        itemsPerPage: event,
      });
    }
  }
  onClientsPageSizeChange(event: any): void {
  const pageSize = event.value ?? event;
  this.clientPageCount = pageSize;
  sessionStorage.setItem('clientsPageSize', pageSize.toString());
  this.clientPage = 1; 

  this.getWorkspaceOwnersClients({
    pageNumber: this.clientPage,
    itemsPerPage: pageSize,
  });
}
onVendorsPageSizeChange(event: any): void {
  const pageSize = event.value ?? event;
  this.clientPageCount = pageSize;
  sessionStorage.setItem('vendorsPageSize', pageSize.toString());
  this.vendorPage = 1; 

  this.getWorkspaceOwnersVendors({
    pageNumber: this.vendorPage,
    itemsPerPage: pageSize,
  });
}
  pageSizeEmitter(index: number): void {
    if (!this.isSearching) {
      paginationChange.next(true);
      this.pageCount = this.pageSizeOptions[index];
      sessionStorage.setItem('pageSize', this.pageCount);
      this.getWorkspaceOwnersProjects({
        pageNumber: this.page,
        itemsPerPage: this.pageCount,
      });
      setTimeout(() => {
        this.projectsSection?.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 200)
    }
  }
  paginationEmitter(params: any) {
    if (!this.isSearching) {
      paginationChange.next(true);
      this.getWorkspaceOwnersProjects(params);
    }
  }
  pageSixeChange(event: any) {
    const pageCount = sessionStorage.getItem('pageSize');
    if (pageCount) {
      this.pageSize = pageCount;
    } else {
      this.pageSize = event;
    }
    this.getWorkspaceOwnersProjects();
  }
  getWorkspaceOwnerDetails(workspaceId: any) {
    this.loaderView = true;
    this.commonHttpService.getWorkspaceOwnnerDetails(workspaceId).subscribe({
      next: (response: any) => {
        this.workspaceOwnerDetails = Object(response).data;
        this.displayedColumnsCount.assetsLinked =
          this.workspaceOwnerDetails!.assetCount;

        this.displayedColumnsCount.projects =
          this.workspaceOwnerDetails!.projectCount;

        this.updateTabCounts();
        this.loaderView = false;
      },
      error: (error: any) => {
        this.loaderView = false;
      },
    });
  }

  getWorkspaceOwnersProjects(param?: any) {
    this.page = (param?.pageNumber ?? 0) > 0 ? param.pageNumber : 1;
    const params = {
      workspaceId: this.workspaceId || '',
      searchColumn: this.searchColumn || '',
      searchText: this.searchText || '',
      workSpaceStatus: this.status,
      pageNumber: (param?.pageNumber ?? 0) > 0 ? param.pageNumber : 1,
      itemsPerPage: this.pageCount || '5',
      sortColumn: param?.sortColumn,
      sortDirection: param?.sortDirection,
    };

    const loadRequest = this.commonHttpService.getOwnersProjects(params);
    if (loadRequest) {
      loadRequest.subscribe({
        next: (response: any) => {
          const projectData = Object(response).data;
          this.tableDetails = (projectData.projects || []).map((project: any) => ({
            id: project.id,
            clientName: project.client?.name,
            name: project.name,
            projects: 1,
            assetLinked: project.assetCount,
            createdByUser: project.createdByUser?.firstName || 'N/A',
            createdOn: new Date(project.createdAt).toLocaleDateString()
          }));
          this.dataSource = (projectData.projects || []).map((project: any) => ({
            id: project.id,
            clientName: project.client?.name,
            name: project.name,
            projects: 1,
            assetLinked: project.assetCount,
            createdByUser: project.createdByUser?.firstName || 'N/A',
            createdOn: new Date(project.createdAt).toLocaleDateString()
          }));
          this.cdr.detectChanges();
          const groupedByClient: { [clientName: string]: any[] } = {};
        for (const project of this.tableDetails) {
          const client = project.clientName;
          if (!groupedByClient.hasOwnProperty(client)) {
            groupedByClient[client] = [];
          }
          groupedByClient[client].push(project);
        }
          this.count = projectData.count || this.tableDetails.length;
          this.commonService.searchParam.next({
            searchColumn: params?.searchColumn,
            searchText: params?.searchText,
            sortColumn: params?.sortColumn,
            sortDirection: params?.sortDirection,
            pageNumber: params?.pageNumber,
            pageSize: this.pageSize,
          });
          this.loaderView = false
        },
        error: () => {
          this.loaderView = false
        }
      })
      this.currentPageNumber = params?.pageNumber;
      this.sortDigit = this.sortDigit === 'DESC' ? 'ASC' : 'DESC';
      this.cdr.detectChanges();
    }
  }

  getWorkspaceOwnersVendors(param?: any) {
  const params = {
    searchColumn: this.searchColumn,
    searchText: this.searchText,
    workspaceId: this.workspaceId || '',
    pageNumber: (param?.pageNumber ?? 0) > 0 ? param.pageNumber : this.vendorPage,
    itemsPerPage: this.vendorPageCount,
    sortColumn: param?.sortColumn,
    sortDirection: param?.sortDirection,
  };

  this.commonHttpService.getOwnersVendors(params).subscribe({
    next: (response: any) => {
      if (response?.data?.rows) {
          this.vendorsDataSource = response.data.rows.map((vendor: any) => ({
            id: vendor.id,
            name: vendor.name,
            email: vendor.email,
            phoneNumber: vendor.phoneNumber,
          }))

        this.vendorCount = response.data.count || response.data.rows.length;

        const newPageSize: number = Number(params.itemsPerPage);
        this.vendorTotalPages = Math.ceil(this.vendorCount / newPageSize);

        if (this.vendorPage > this.vendorTotalPages && this.vendorTotalPages > 0) {
          this.vendorPage = 1;
          this.getWorkspaceOwnersVendors({ 
            pageNumber: this.vendorPage, 
            itemsPerPage: newPageSize 
          });
          return;
        }

        this.cdr.detectChanges();
        this.isVendorSearching = false;
      } else {
        this.vendorCount = 0;
        this.cdr.detectChanges();
        this.isVendorSearching = false;
      }
    },
    error: (error: any) => {
      this.isVendorSearching = false;
      console.error('Error fetching vendors:', error);
    },
  })
}


  getWorkspaceOwnersClients(param?: any) {
    this.clientPage = param?.pageNumber ?? this.clientPage ?? 1;
    const params = {
      searchColumn: this.searchColumn,
      searchText: this.searchText,
      workspaceId: this.workspaceId || '',
      pageNumber: this.clientPage,
      itemsPerPage: this.clientPageCount,
      sortColumn: param?.sortColumn,
      sortDirection: param?.sortDirection,
    };
    this.commonHttpService.getOwnersClients(params).subscribe({
      next: (response: any) => {
        if (response?.data?.rows) {
          this.clientTableDetails = response.data.rows.map((client: any) => ({
            client: {
              clientName: client.name,
              createdAt: client.createdAt
            },
            user: {
              createdByUser: client.createdByUser ? `${client.createdByUser?.firstName ?? ''} ${client.createdByUser?.lastName ?? ''}` : 'N/A'
            },
            id: client.id
          }));
          this.clientCount = response.data.count || response.data.rows.length;
          const newPageSize: number = Number(params.itemsPerPage);
          this.clientTotalPages = Math.ceil(this.clientCount / newPageSize);
          if (this.clientPage > this.clientTotalPages && this.clientTotalPages > 0) {
              this.clientPage = 1;
              this.getWorkspaceOwnersClients({ 
                  pageNumber: this.clientPage, 
                  itemsPerPage: newPageSize 
              });
              return; 
          }
          this.cdr.detectChanges();
          this.isClientSearching = false;
        } else {
          this.clientTableDetails = [];
          this.clientCount = 0;
          this.cdr.detectChanges();
          this.isClientSearching = false;
        }
      },
      error: (error: any) => {
        this.isClientSearching = false;
        console.error('Error fetching clients:', error);
      },
    });
  }

  vendorPaginationEmitter(params: any) {
    if (!this.isSearching) {
      paginationChange.next(true);
      this.getWorkspaceOwnersVendors(params);

    }
  }

  vendorPageSizeEmitter(index: number): void {
    if (!this.isSearching) {
      this.vendorPageCount = this.pageSizeOptions[index];
      sessionStorage.setItem('vendorPageSize', this.vendorPageCount);
      this.getWorkspaceOwnersVendors({
        pageNumber: this.page,
        itemsPerPage: this.vendorPageCount,
      });
      setTimeout(() => {
        this.vendorSection?.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 200)
    }
  }

  onTabChange(tabValue: string) {
    if (!this.isSearching) {
      this.selectedUserTab = tabValue;
      this.userPageCount = '5'; 
      this.getUsersList({
        pageNumber: this.page,
        itemsPerPage: this.userPageCount,
        roleId: this.selectedUserTab,
      });
    }
  }

  getUsersList(param?: any) {
    const params = {
      workspaceId: this.workspaceId,
      pageNumber: param?.pageNumber || '1',
      itemsPerPage: this.userPageCount,
      sortColumn: param?.sortColumn,
      sortDirection: param?.sortDirection,
      searchColumn: this.searchColumn,
      searchText: this.searchText,
      roleId: this.selectedUserTab, // Ensure roleId is set to selectedUserTab
    };
    this.commonHttpService.getWorkspaceUsers(params).subscribe({
      next: (response: any) => {
        if (response?.data?.rows) {
          this.usersDataSource = response.data.rows.map((user: any) => ({
            id: user.id,
            name: user.firstName + ' ' + user.lastName,
            employeeId: user.employeeId,
            designation: user.employeeDesignation?.attributeValue || 'N/A',
            email: user.email,
            reportingManager: user.reportingManagerDetails ? `${user.reportingManagerDetails.firstName} ${user.reportingManagerDetails.lastName}` : "N/A",
            profileImage: user.profilePictureUrl,
            isPrimary: user.user_role.isPrimary
          }));
          this.userCount = response.data.count;
          this.cdr.detectChanges();
        }
        this.isSearching = false; // Reset the flag
      },
      error: (error: any) => {
        this.isSearching = false; // Reset the flag on error
      },
    });
  }
  userPageSizeEmitter(index: number): void {
    if (!this.isSearching) {
      paginationChange.next(true);
      this.userPageCount = this.pageSizeOptions[index];
      sessionStorage.setItem('userPageSize', this.userPageCount);
      this.getUsersList({
        pageNumber: this.page,
        itemsPerPage: this.userPageCount,
        roleId: this.selectedUserTab,
      });
    }
  }
  userPaginationEmitter(params: any) {
    if (!this.isSearching) {
      paginationChange.next(true);
      this.getUsersList({
        ...params,
        roleId: this.selectedUserTab,
      });
    }
  }
  getAllClients(){
    const params = {
      pageNumber: 1,
      itemsPerPage: 1000
    };
    this.commonHttpService.getOwnersClients(params).subscribe({
      next: (response: any) => {
        this.clientsList = response.data.rows.map((client: any) => (client.name));
          },
      error: (error: any) => {
        this.isClientSearching = false;
        console.error('Error fetching clients:', error);
      },
    });
  }

  updateTabCounts() {
    if (this.workspaceOwnerDetails) {
      this.userTabs = this.userTabs.map((tab) => {
        let count = 0;
        switch (tab.value) {
          case 'WORKSPACE-OWNER':
            count = this.workspaceOwnerDetails!.workspaceOwnerCount || 0;
            break;
          case 'ADMIN':
            count = this.workspaceOwnerDetails!.adminCount || 0;
            break;
          case 'USER':
            count = this.workspaceOwnerDetails!.userCount || 0;
            break;
        }
        return { ...tab, count };
      });
      this.cdr.detectChanges();
    }
  }
  onSearch(searchData: any) {
    this.isSearching = true;
    this.searchColumn = searchData.filterBy;
    this.searchText = searchData.searchStr;
    this.getWorkspaceOwnersProjects({
      pageNumber: this.page,
      itemsPerPage: this.pageCount,
      searchColumn: this.searchColumn,
      searchText: this.searchText
    });
  }

  onVendorSearch(searchData: any) {
    this.isSearching = true;
    this.searchColumn = searchData.searchBy;
    this.searchText = searchData.searchText;
    this.getWorkspaceOwnersVendors({
      pageNumber: this.page,
      itemsPerPage: this.vendorPageCount,
      searchColumn: this.searchColumn,
      searchText: this.searchText,
    });
  }

  onClientSearch(searchData: any) {
    this.isSearching = true;
    this.searchColumn = searchData.searchBy;
    this.searchText = searchData.searchText;
    this.getWorkspaceOwnersClients({
      pageNumber: this.page,
      itemsPerPage: this.pageCount,
      searchColumn: this.searchColumn,
      searchText: this.searchText
    });
  }

  onUserSearch(searchData: any) {
    this.isSearching = true;
    this.searchColumn = searchData.searchBy;
    this.searchText = searchData.searchText;
    this.getUsersList({
      pageNumber: this.page,
      itemsPerPage: this.userPageCount,
      searchColumn: this.searchColumn,
      searchText: this.searchText,
      roleId: this.selectedUserTab, 
    });
  }

  getErrorKeys(controlName: string): string[] {
    const form = this.addFormData?.form?.confirmationForm;
    const control = form?.get(controlName);
    return control && control.errors ? Object.keys(control.errors) : [];
  }
  filterProjects(filter: any) {
    this.status = filter;
    this.getWorkspaceOwnersProjects();
  }
  filterClients(filter: any) {
    this.status = filter;
    this.getWorkspaceOwnersClients();
  }
  filterVendors(filter: any) {
    this.status = filter;
    this.getWorkspaceOwnersVendors();
  }
  getFilteredData(event: any) {
    this.getWorkspaceOwnersProjects()
  }
  onAddVendor() {
    this.addFormData = {
      header: 'Add Vendor',
      description: 'Please enter the vendor details',
      image: 'cautionImage',
      form: {
        confirmationForm: new UntypedFormGroup({
          name: new UntypedFormControl('', [Validators.required, Validators.pattern(/^[A-Za-z]+(?: [A-Za-z]+)*$/)]),
          email: new UntypedFormControl('', [Validators.required, Validators.email, Validators.pattern(/^\S.*\S$|^\S$/)]),
          phoneNumber: new UntypedFormControl('', [Validators.required, Validators.pattern(/^\d{10}$/), Validators.pattern(/^\S.*\S$|^\S$/)]),
        }),
      },
      fields: [
        {
          field: 'text-field',
          controlName: 'name',
          placeholder: 'Enter Vendor Name',
          label: 'Vendor Name',
          error: 'This is a required field'
        },
        {
          field: 'text-field',
          controlName: 'email',
          placeholder: 'Enter Vendor Email',
          label: 'Vendor Email',
          error: 'Please enter valid email'
        },
        {
          field: 'text-field',
          controlName: 'phoneNumber',
          placeholder: 'Enter Vendor Phone Number',
          label: 'Vendor Phone Number',
          error: 'This is a required field'
        },
      ],
      secondaryBtn: 'Cancel',
      primaryBtn: 'Add',
    };

    const dialogRef = this.dialog.open(ConfirmationPopupWithFormComponent, {
      panelClass: 'center-dialog-box',
      backdropClass: 'backdrop',
      disableClose: true,
      width: '50%',
      data: this.addFormData,
    });

    dialogRef.closed.subscribe((result: any) => {
      if (result) {
        const payload = {
          name: result.name,
          email: result.email,
          phoneNumber: result.phoneNumber,
        };

        this.commonHttpService.addWorkspaceVendor(payload).subscribe({
          next: (response: any) => {
            this.toastrService.success(`Vendor '${payload.name}' is successfully added!`, '', {
              toastClass: 'toaster-wrapper',
            });

            this.getWorkspaceOwnersVendors();
          },
          error: (error) => {
            const errorMsg = error?.error?.error?.errorList || 'Something went wrong!';
            this.toastrService.error(errorMsg, '', {
              toastClass: 'toaster-wrapper',
            });
          },
        });
      }
    });
  }
  onAddProject(): void {
  this.openAddProjectDialog();
}
  openAddProjectDialog(){
    this.getAllClients();
    const formData = {
    header: 'Add Project Name',
    description: 'Please enter the project name',
    image: 'cautionImage',
    loading: this.isProjectModalLoading, 
    form: {
      confirmationForm: new UntypedFormGroup({
        addProject: new UntypedFormControl('', [Validators.required, Validators.pattern(/^\S.*$|^\S$/)]),
        client: new UntypedFormControl('', [Validators.required]),
      }),
    },
    fields: [
      {
        field: 'text-field',
        controlName: 'addProject',
        placeholder: 'Enter Project Name',
        label: 'Project Name',
        error: 'Enter a valid project name'
      },
      {
        field: 'dropdown',
        controlName: 'client',
        options: [...this.clientsList],
        title: 'Select Client',
        shouldEmitOption: true,
        label: 'Client',
        labelType: 'text-label',
        isClient: true,
        addClientEmitter: this.addClientClick$, 
      },
    ],
    secondaryBtn: 'Cancel',
    primaryBtn: 'Add',
  };

  const dialogRef = this.dialog.open(ConfirmationPopupWithFormComponent, {
    panelClass: 'center-dialog-box',
    backdropClass: 'backdrop',
    disableClose: true,
    width: '50%',
    data: formData,
  });

  const sub = this.addClientClick$.subscribe(() => {
    this.isAddingClient = true;
    dialogRef.close(); 
    this.onAddClient(true); 
  });

  dialogRef.closed.subscribe((result: any) => {
    sub.unsubscribe(); 
    if (result) {
      const selectedClientName = result.client; 
      const selectedClient = this.clientTableDetails.find(
        c => c.client.clientName === selectedClientName
      );
      const projectData = {
        name: Object(result).addProject,
        clientId: selectedClient.id,
      };

      this.commonHttpService.addWorkspaceProject(projectData).subscribe({
        next: (response: any) => {
          if (response?.statusCode === 200) {
            this.toastrService.success(
              `Project '${projectData.name}' is successfully added`,
              '',
              { toastClass: 'toaster-wrapper' }
            );
            this.getWorkspaceOwnersProjects(this.workspaceId);
          }
        },
        error: (error) => {
          const errorMsg = error?.error?.error?.errorList || 'Something went wrong!';
          this.toastrService.error(errorMsg, '', { toastClass: 'toaster-wrapper' });
        },
      });
    }
  });
  }
  onEditProject(project: any) {
    const formData = {
      header: 'Edit Project Name',
      description: 'Please update the project name',
      image: 'cautionImage',
      form: {
        confirmationForm: new UntypedFormGroup({
          editProject: new UntypedFormControl(project.name || '', [
            Validators.required, Validators.pattern(/^\S.*\S$|^\S$/),
            this.notSameAs(project.name)
          ]),
        }),
      },
      fields: [
        {
          field: 'text-field',
          controlName: 'editProject',
          placeholder: 'Enter Project Name',
          label: 'Project Name',
          error: 'Enter a valid project name',
          errorMessages: {
            required: 'Project name is required',
            sameValue: 'Project name cannot be the same as current one',
            pattern: 'Don’t add spaces at the beginning or end'
          },
        },
      ],
      secondaryBtn: 'Cancel',
      primaryBtn: 'Update',
    };

    const dialogRef = this.dialog.open(ConfirmationPopupWithFormComponent, {
      panelClass: 'center-dialog-box',
      backdropClass: 'backdrop',
      disableClose: true,
      width: '50%',
      data: formData,
    });

    dialogRef.closed.subscribe((result) => {
      if (result) {
        const updatedProjectData = {
          name: Object(result).editProject,
        };

        this.commonHttpService
          .updateWorkspaceProject(project.id, updatedProjectData)
          .subscribe({
            next: (response: any) => {
              if (response.statusCode === 200) {
                this.toastrService.success(
                  `Project '${updatedProjectData.name}' is successfully updated`,
                  '',
                  {
                    toastClass: 'toaster-wrapper',
                  }
                );
                this.getWorkspaceOwnersProjects();
                // Refresh the project list or perform any additional actions
              }
            },
            error: (error) => {
              const errorMsg = error?.error.error.errorList || 'Something went wrong!';
              this.toastrService.error(errorMsg, '', {
                toastClass: 'toaster-wrapper',
              });
            },
          });
      }
    });
  }
  onEditVendor(vendor: any) {
    const clean = (val: string) => (val || '').trim().toLowerCase();
    const initialValues = {
      name: vendor.name || '',
      email: vendor.email || '',
      phoneNumber: vendor.phoneNumber || '',
    };
    const cleanedInitialValues = {
      name: clean(initialValues.name),
      email: clean(initialValues.email),
      phoneNumber: clean(initialValues.phoneNumber),
    };

    const formGroup = new UntypedFormGroup({
      name: new UntypedFormControl(initialValues.name, [
        Validators.required,
        Validators.pattern(/^\S.*\S$|^\S$/)
      ]),
      email: new UntypedFormControl(initialValues.email, [
        Validators.required,
        Validators.email
      ]),
      phoneNumber: new UntypedFormControl(initialValues.phoneNumber, [
        Validators.required,
        Validators.pattern(/^\d{10}$/)
      ]),
    });
    const hasChanges = () => {
      return (
        clean(formGroup.get('name')?.value) !== cleanedInitialValues.name ||
        clean(formGroup.get('email')?.value) !== cleanedInitialValues.email ||
        clean(formGroup.get('phoneNumber')?.value) !== cleanedInitialValues.phoneNumber
      );
    };

    const formData = {
      header: 'Edit Vendor Details',
      description: 'Please update vendor information',
      image: 'cautionImage',
      form: {
        confirmationForm: formGroup,
      },
      fields: [
        {
          field: 'text-field',
          controlName: 'name',
          placeholder: 'Enter Vendor Name',
          label: 'Name',
          errorMessages: {
            required: 'Name is required',
            sameValue: 'Name is same as current',
            pattern: 'Don’t add spaces at the beginning or end',
          },
        },
        {
          field: 'text-field',
          controlName: 'email',
          placeholder: 'Enter Vendor Email',
          label: 'Email',
          errorMessages: {
            required: 'Email is required',
            sameValue: 'Email is same as current',
            email: 'Enter a valid email address',
          },
        },
        {
          field: 'text-field',
          controlName: 'phoneNumber',
          placeholder: 'Enter Phone Number',
          label: 'Phone',
          errorMessages: {
            required: 'Phone number is required',
            sameValue: 'Phone is same as current',
            pattern: 'Enter a valid 10-digit number',
          },
        },
      ],
      secondaryBtn: 'Cancel',
      primaryBtn: 'Update',
      disablePrimaryBtn: true, // initially disable
    };

    const dialogRef = this.dialog.open(ConfirmationPopupWithFormComponent, {
      panelClass: 'center-dialog-box',
      backdropClass: 'backdrop',
      disableClose: true,
      width: '50%',
      data: formData,
    });

    // Enable/disable Update button based on changes
    formGroup.valueChanges.subscribe(() => {
      const changed = hasChanges();
      const valid = formGroup.valid;
      (dialogRef.componentInstance!.data as any).disablePrimaryBtn = !(changed && valid);
      dialogRef.componentInstance!.changeDetectorRef.detectChanges(); // force re-render if needed
    });

    dialogRef.closed.subscribe((result: any) => {
      if (result && hasChanges()) {
        const updatedVendorData = {
          name: result.name,
          email: result.email,
          phone: result.phoneNumber,
        };

        this.commonHttpService
          .updateVendor(vendor.id, updatedVendorData) // replace with actual service method
          .subscribe({
            next: (response: any) => {
              if (response.statusCode === 200) {
                this.toastrService.success(
                  `Vendor '${updatedVendorData.name}' successfully updated`,
                  '',
                  { toastClass: 'toaster-wrapper' }
                );
                this.getWorkspaceOwnersVendors()
              }
            },
            error: (error) => {
              const errorMsg = error?.error?.error?.errorList || 'Something went wrong!';
              this.toastrService.error(errorMsg, '', {
                toastClass: 'toaster-wrapper',
              });
            },
          });
      }
    });
  }
  onEditClient(client: any) {
    const clientName = client.client.clientName;
    const formData = {
      header: 'Edit Client Name',
      description: 'Please enter the client name',
      image: 'cautionImage',
      form: {
        confirmationForm: new UntypedFormGroup({
         editClient: new UntypedFormControl(clientName || '', [
            Validators.required, Validators.pattern(/^\S.*\S$|^\S$/),
            this.notSameAs(clientName)
          ]),
        }),
      },
      fields: [
        {
          field: 'text-field',
          controlName: 'editClient',
          placeholder: 'Enter Client Name',
          label: 'Client Name',
          error: 'Enter a valid client name',
          errorMessages: {
            required: 'Client name is required',
            sameValue: 'Client name cannot be the same as current one',
            pattern: 'Don’t add spaces at the beginning or end'
          },
        },
      ],
      secondaryBtn: 'Cancel',
      primaryBtn: 'Update',
    };

    const dialogRef = this.dialog.open(ConfirmationPopupWithFormComponent, {
      panelClass: 'center-dialog-box',
      backdropClass: 'backdrop',
      disableClose: true,
      width: '50%',
      data: formData,
    });

    dialogRef.closed.subscribe((result) => {
      if (result) {
        const clientData = {
          name: Object(result).editClient,
        };

        this.commonHttpService.updateWorkspaceClient(clientData, client.id).subscribe({
          next: (response: any) => {
            if (response?.statusCode === 200) {
              this.toastrService.success(
                `Client '${clientData.name}' is successfully Updated`,
                '',
                {
                  toastClass: 'toaster-wrapper',
                }
              );
              this.getWorkspaceOwnersClients();
            }
          },
          error: (error) => {
            const errorMsg =
              error?.error?.error?.errorList || 'Something went wrong!';
            this.toastrService.error(errorMsg, '', {
              toastClass: 'toaster-wrapper',
            });
          },
        });
      }
    });
  }
  onDeleteProject(project: any) {
    const formData = {
      header: 'Delete Project',
      description: `Are you sure you want to delete the project ${project.name}? This action cannot be undone.`,
      image: 'failureImage',
      form: {
        confirmationForm: new UntypedFormGroup({}),
      },
      fields: [], // No input field required for simple confirmation
      secondaryBtn: 'Cancel',
      primaryBtn: 'Delete',
    };

    const dialogRef = this.dialog.open(ConfirmationPopupWithFormComponent, {
      panelClass: 'center-dialog-box',
      backdropClass: 'backdrop',
      disableClose: true,
      width: '60%',
      data: formData,
    });

    dialogRef.closed.subscribe((confirmed) => {
      if (confirmed) {
        this.commonHttpService.deleteProject(project.id).subscribe({
          next: (response: any) => {
            if (response.statusCode === 200) {
              this.toastrService.success(
                `Project '${project.name}' has been deleted successfully`,
                '',
                {
                  toastClass: 'toaster-wrapper',
                }
              );
              this.getWorkspaceOwnersProjects(); // Refresh list
            }
          },
          error: (error) => {
            const errorMsg = error?.error?.error?.errorList || 'Something went wrong!';
            this.toastrService.error(errorMsg, '', {
              toastClass: 'toaster-wrapper',
            });
          },
        });
      }
    });
  }
  onAddClient(fromAddProject = false) {
  const formData = {
    header: 'Add Client Name',
    description: 'Please enter the client name',
    image: 'cautionImage',
    form: {
      confirmationForm: new UntypedFormGroup({
        name: new UntypedFormControl('', [
          Validators.required,
          Validators.pattern(/^\S.*$|^\S$/),
        ]),
      }),
    },
    fields: [
      {
        field: 'text-field',
        controlName: 'name',
        placeholder: 'Enter Client Name',
        label: 'Client Name',
        error: 'Enter a valid client name',
      },
    ],
    secondaryBtn: 'Cancel',
    primaryBtn: 'Add',
  };

  const dialogRef = this.dialog.open(ConfirmationPopupWithFormComponent, {
    panelClass: 'center-dialog-box',
    backdropClass: 'backdrop',
    disableClose: true,
    width: '50%',
    data: formData,
  });

  dialogRef.closed.subscribe((result) => {
    if (!result) {
      if (fromAddProject) {
        this.openAddProjectDialog();
      }
      return;
    }

    const clientData = { name: Object(result).name };

    this.isProjectModalLoading = true;

    this.commonHttpService.addWorkspaceClient(clientData).subscribe({
      next: (response: any) => {
        this.isProjectModalLoading = false; 

        if (response?.statusCode === 200) {
          this.toastrService.success(
            `Client '${clientData.name}' is successfully added`,
            '',
            { toastClass: 'toaster-wrapper' }
          );

          this.getAllClients();
          this.getWorkspaceOwnersClients();
          if (fromAddProject) {
            setTimeout(() => this.openAddProjectDialog(), 100);
          }
        }
      },
      error: (error: any) => {
        this.isProjectModalLoading = false; 
        const errorMsg =
          error?.error?.error?.errorList || 'Something went wrong!';
        this.toastrService.error(errorMsg, '', {
          toastClass: 'toaster-wrapper',
        });

        if (fromAddProject) {
          setTimeout(() => this.openAddProjectDialog(), 100);
        }
      },
    });
  });
}
onDeleteVendor(vendor: any) {
    const formData = {
      header: 'Delete Vendor',
      description: `Are you sure you want to delete the vendor '${vendor.name}'? This action cannot be undone.`,
      image: 'failureImage', 
      form: {
        confirmationForm: new UntypedFormGroup({}),
      },
      fields: [], 
      secondaryBtn: 'Cancel',
      primaryBtn: 'Delete',
    };
    const dialogRef = this.dialog.open(ConfirmationPopupWithFormComponent, {
      panelClass: 'center-dialog-box',
      backdropClass: 'backdrop',
      disableClose: true,
      width: '60%',
      data: formData,
    });
    dialogRef.closed.subscribe((confirmed) => {
      if (confirmed) {
        this.commonHttpService.deleteVendor(vendor.id).subscribe({
          next: (response: any) => {
            if (response.statusCode === 200) {
              this.toastrService.success(
                `Vendor '${vendor.name}' has been deleted successfully`,
                '',
                {
                  toastClass: 'toaster-wrapper',
                }
              );
              this.getWorkspaceOwnersVendors(); 
            }
          },
          error: (error) => {
            const errorMsg = error?.error?.error?.errorList || 'Something went wrong!';
            this.toastrService.error(errorMsg, '', {
              toastClass: 'toaster-wrapper',
            });
          },
        });
      }
    });
}
 onDeleteCLient(client: any) {
    const clientName = client.client.clientName;
    const clientId = client.id;
    const formData = {
      header: 'Delete Client',
      description: `Are you sure you want to delete the Client ${clientName}? This action cannot be undone.`,
      image: 'failureImage',
      form: {
        confirmationForm: new UntypedFormGroup({}),
      },
      fields: [], // No input field required for simple confirmation
      secondaryBtn: 'Cancel',
      primaryBtn: 'Delete',
    };

    const dialogRef = this.dialog.open(ConfirmationPopupWithFormComponent, {
      panelClass: 'center-dialog-box',
      backdropClass: 'backdrop',
      disableClose: true,
      width: '60%',
      data: formData,
    });

    dialogRef.closed.subscribe((confirmed) => {
      if (confirmed) {
        this.commonHttpService.deleteWorkspaceClient(clientId).subscribe({
          next: (response: any) => {
            if (response.statusCode === 200) {
              this.toastrService.success(
                `Client '${clientName}' has been deleted successfully`,
                '',
                {
                  toastClass: 'toaster-wrapper',
                }
              );
              this.getWorkspaceOwnersClients(); // Refresh list
            }
          },
          error: (error) => {
            const errorMsg = error?.error?.error?.errorList || 'Something went wrong!';
            this.toastrService.error(errorMsg, '', {
              toastClass: 'toaster-wrapper',
            });
          },
        });
      }
    });
  }
  onAddAssetType(): void {
    const formData = {
      header: 'Add Asset Type',
      description: 'Please enter the details to proceed',
      image: 'cautionImage',
      form: {
        confirmationForm: new UntypedFormGroup({
          name: new UntypedFormControl('', [Validators.required]),
          assetTypeCode: new UntypedFormControl('', [Validators.required]),

        }),
      },
      fields: [
        {
          field: 'text-field',
          controlName: 'name',
          placeholder: 'Enter Asset Type Name',
          label: 'Asset Type Name',
          error: 'Please enter a valid Asset Type Name (Eg - Laptop)'
        },
        {
          field: 'text-field',
          controlName: 'assetTypeCode',
          placeholder: 'Enter Asset Type Code',
          label: 'Asset Type Code',
          error: 'Please enter a valid Asset Type Code'
        }

      ],
      secondaryBtn: 'Cancel',
      primaryBtn: 'Add',
    };

    const dialogRef = this.dialog.open(ConfirmationPopupWithFormComponent, {
      panelClass: 'center-dialog-box',
      backdropClass: 'backdrop',
      disableClose: true,
      width: '50%',
      data: formData,
    });

    dialogRef.closed.subscribe((result: any) => {
      if (result) {
        const payload = {
          name: result.name,
          assetTypeCode: result.assetTypeCode,
          icon: null,
        };
        this.loaderView = true;
        this.commonHttpService.addAssetTypes(payload).subscribe({
          next: (response: any) => {
            // this.toastrService.success(`Asset Type '${response.name}' added successfully`, '', {
            //   toastClass: 'toaster-wrapper',
            // });
            // Redirect to the add asset page with the response data
            this.showAddAssetType = true
            this.router.navigate(['add-asset-type'], {
              relativeTo: this.activatedRoute,
              queryParams: {
                name: response.data.name,
                assetTypeCode: response.data.assetTypeCode,
                id: response.data.id
              },
            });
            this.loaderView = false;
          },
          error: (error) => {
            const errorMsg = error?.error?.error?.errorList || error?.error?.message || 'Something went wrong!';
            this.toastrService.error(errorMsg, '', {
              toastClass: 'toaster-wrapper',
            });
            this.loaderView = false;
          },
        });
      }
    });
  }

  onAddAccessoryType(): void {
    const formData = {
      header: 'Add Accessory Type',
      description: 'Please enter the details to proceed',
      image: 'cautionImage',
      form: {
        confirmationForm: new UntypedFormGroup({
          name: new UntypedFormControl('', [Validators.required]),
        }),
      },
      fields: [
        {
          field: 'text-field',
          controlName: 'name',
          placeholder: 'Enter Accessory Type Name',
          label: 'Accessory Type Name',
          error: 'Please Enter a valid Accessory Name'
        },
      ],
      secondaryBtn: 'Cancel',
      primaryBtn: 'Add',
    };

    const dialogRef = this.dialog.open(ConfirmationPopupWithFormComponent, {
      panelClass: 'center-dialog-box',
      backdropClass: 'backdrop',
      disableClose: true,
      width: '50%',
      data: formData,
    });

    dialogRef.closed.subscribe((result: any) => {
      if (result) {
        const payload = {
          name: result.name,
          icon: null,
        };
        this.commonHttpService.addAccessoryTypes(payload).subscribe({
          next: (response: any) => {
            // this.toastrService.success(`Accessory Type '${response.name}' added successfully`, '', {
            //   toastClass: 'toaster-wrapper',
            // });
            // Redirect to the add accessory page with the response data
            this.showAddAccessoryType = true;
            this.router.navigate(['add-accessory-type'], {
              relativeTo: this.activatedRoute,
              queryParams: {
                name: response.data.name,
                id: response.data.id
              },
            });
          },
          error: (error) => {
            const errorMsg = error?.error?.error?.message || error?.message || 'Something went wrong!';
            this.toastrService.error(errorMsg, '', {
              toastClass: 'toaster-wrapper',
            });
          },
        });
      }
    });
  }

  onAddMoreAcronyms(): void {
    const formData = {
      header: 'Add Asset Code Acronym',
      description: 'Please enter the Asset Code Acronym',
      image: 'cautionImage',
      form: {
        confirmationForm: new UntypedFormGroup({
          acronym: new UntypedFormControl('', [Validators.required]),
        }),
      },
      fields: [
        {
          field: 'text-field',
          controlName: 'acronym',
          placeholder: 'Enter Acronym',
          label: 'Acronym',
          isAcronym: true
        },
      ],
      secondaryBtn: 'Cancel',
      primaryBtn: 'Add',
    };

    const dialogRef = this.dialog.open(ConfirmationPopupWithFormComponent, {
      panelClass: 'center-dialog-box',
      backdropClass: 'backdrop',
      disableClose: true,
      width: '50%',
      data: formData,
    });

    dialogRef.closed.subscribe((result: any) => {
      if (result) {
        const acronym = result.acronym.toUpperCase();
        const payload = { acronyms: [acronym] };


        this.commonHttpService.addAcronyms(payload).subscribe({
          next: (response: any) => {
            this.toastrService.success(`Acronym '${acronym}' is successfully added!`, '', {
              toastClass: 'toaster-wrapper',
            });

            this.getWorkspaceOwnerDetails(this.workspaceId)
            // Refresh the list or perform additional actions
          },
          error: (error) => {
            const errorMsg = error.error.error?.errorList || 'Something went wrong!';
            this.toastrService.error(errorMsg, '', {
              toastClass: 'toaster-wrapper',
            });
          },
        });
      }
    });
  }

  onAssetTypeClick(assetTypeId: string) {
    //  this.router.navigate(['asset-type', assetTypeId], { relativeTo: this.activatedRoute });
    const dialogRef = this.dialog.open(AssetTypeDetailComponent, {
      panelClass: 'center-dialog-box',
      backdropClass: 'backdrop',
      disableClose: true,
      width: '50%',
      data: { id: assetTypeId }
    });
    dialogRef.closed.subscribe((result: any) => {
      // Handle any actions after the dialog is closed
    });
  }

  onAccessoryTypeClick(assetTypeId: string) {
    //  this.router.navigate(['asset-type', assetTypeId], { relativeTo: this.activatedRoute });
    const dialogRef = this.dialog.open(AccessoryTypeDetailComponent, {
      panelClass: 'center-dialog-box',
      backdropClass: 'backdrop',
      disableClose: true,
      width: '50%',
      data: { id: assetTypeId }
    });
    dialogRef.closed.subscribe((result: any) => {
      // Handle any actions after the dialog is closed
    });
  }

  onUserClick(userId: string) {
    const dialogRef = this.dialog.open(UserDetailsComponent, {
      panelClass: 'center-dialog-box',
      backdropClass: 'backdrop',
      disableClose: true,
      width: '43rem',
      data: { id: userId }
    });
    dialogRef.closed.subscribe((result: any) => {

      let formData = {
        header: "Edit User's Role",
        description: 'Please edit the user role below',
        image: 'cautionImage',
        form: {
          confirmationForm: new UntypedFormGroup({
            editRole: new UntypedFormControl('', [Validators.required]),
          })
        },
        fields: [
          {
            field: 'dropdown',
            controlName: 'editRole',
            title: 'Select Role',
            shouldEmitOption: true,
            label: 'User Role',
            options: [] as string[]
          },
        ],
        primaryBtn: 'Update Role',
        secondaryBtn: 'Close'
      }

      if (result === "Edit User Role") {
        this.commonHttpService.getAllUserRoles().subscribe({
          next: (response: any) => {
            const roles = response.data as IRole[];
            let formattedRoles = roles.map(role => convertToTitleCase(role.name));
            formData.fields[0].options = formattedRoles;
            const dialogRef = this.dialog.open(ConfirmationPopupWithFormComponent, {
              panelClass: 'center-dialog-box',
              backdropClass: 'backdrop',
              disableClose: true,
              width: '50%',
              data: formData,
            });
            dialogRef.closed.subscribe((result: any) => {
              if (result) {
                const role = convertToCapitalCase(result.editRole);
                this.commonHttpService.updateUserRole(userId, role).subscribe({
                  next: (response: any) => {
                    if (Object(response).data) {
                      const successResponse = Object(response).data;
                      this.getWorkspaceOwnerDetails(this.workspaceId);
                      this.onTabChange(this.selectedUserTab);

                      this.toastrService.success(
                        successResponse,
                        '',
                        {
                          toastClass: 'toaster-wrapper',
                        }
                      );

                    }
                  },
                  error: (error: any) => {
                    this.toastrService.error(
                      Object(error).error?.error?.errorList ||
                      'Something went wrong!',
                      '',
                      {
                        toastClass: 'toaster-wrapper',
                      }
                    );
                  }
                });
              }
            });
          },
          error: (error) => {
            console.error(error);
          },
        });
      }
    });
  }

  toTitleCase(data: string) {
    return convertToTitleCase(data);
  }
  notSameAs(original: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value === original) {
        return { sameValue: true };
      }
      return null;
    };
  }

}
